import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { money, date, invoiceTotal } from '../../shared/formatters';
import Breadcrumbs from '../../shared/components/Breadcrumbs';
import EmptyState from '../../shared/components/EmptyState';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import { portalApi } from '../../lib/api';
import { QuoteStatusPill } from './QuotesList';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

// Quote detail (pass 5 §5E) — read view + Send / Decline / Expire +
// Convert-to-Job/Invoice (only when approved, via the Node endpoint —
// idempotent, 409 on double-convert).
export default function QuoteDetail() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const { data, update } = useData();
  const { notify } = useToast();
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState(null); // 'decline' | 'expire'

  const quote = data.quotes.find((item) => item.id === quoteId);
  if (!quote) {
    return <div className="standalone-page"><EmptyState title="Quote not found" action="Back to quotes" onAction={() => navigate('/admin/quotes')} /></div>;
  }

  const client = data.clients.find((item) => item.id === quote.client_id);

  const setStatus = async (status) => {
    setBusy(status);
    try {
      await update('quotes', quote.id, { ...quote, status });
      notify(`Quote marked ${status}`);
      setConfirm(null);
    } catch (err) {
      notify(err.message || 'Update failed', 'error');
    } finally {
      setBusy('');
    }
  };

  const convert = async (target) => {
    setBusy(`convert-${target}`);
    try {
      const result = await portalApi.convertQuote(quote.id, target);
      await update('quotes', quote.id, {
        ...quote,
        converted_job_id: result.job_id ?? quote.converted_job_id,
        converted_invoice_id: result.invoice_id ?? quote.converted_invoice_id,
      });
      notify(
        result.job_id && result.invoice_id ? 'Converted — job and invoice created'
          : result.job_id ? 'Converted — job created'
            : 'Converted — invoice created'
      );
    } catch (err) {
      notify(err.message || 'Conversion failed', 'error');
    } finally {
      setBusy('');
    }
  };

  const convertedJob = quote.converted_job_id ? data.jobs.find((item) => item.id === quote.converted_job_id) : null;
  const convertedInvoice = quote.converted_invoice_id ? data.invoices.find((item) => item.id === quote.converted_invoice_id) : null;

  return (
    <div className="standalone-page">
      <Breadcrumbs items={[{ label: 'Quotes', to: '/admin/quotes' }, { label: quote.number }]} />

      <header className="simple-header">
        <div>
          <span className="eyebrow">{client?.name || 'Client'}</span>
          <h1>{quote.number}</h1>
          <p className="field-hint">Issued {date(quote.issued_at)}{quote.valid_until ? ` · valid until ${date(quote.valid_until)}` : ''}</p>
        </div>
        <div className="header-actions">
          <button className="secondary-button" onClick={() => navigate(`/admin/quotes/${quote.id}/edit`)}>Edit</button>
          {quote.status === 'draft' && (
            <button className="primary-button" onClick={() => setStatus('sent')} disabled={busy === 'sent'}>
              {busy === 'sent' ? 'Sending…' : 'Send to client'}
            </button>
          )}
          {quote.status === 'sent' && (
            <button className="secondary-button" onClick={() => setConfirm('decline')} disabled={Boolean(busy)}>Decline</button>
          )}
          {(quote.status === 'sent' || quote.status === 'approved') && (
            <button className="secondary-button" onClick={() => setConfirm('expire')} disabled={Boolean(busy)}>Expire</button>
          )}
          {quote.status === 'approved' && (
            <>
              <button className="secondary-button" onClick={() => convert('job')} disabled={Boolean(quote.converted_job_id) || Boolean(busy)}>
                {quote.converted_job_id ? <SafeIcon icon={Icons.Check} /> : null} {quote.converted_job_id ? 'Job created' : 'Convert to job'}
              </button>
              <button className="primary-button" onClick={() => convert('both')} disabled={Boolean(quote.converted_job_id && quote.converted_invoice_id) || Boolean(busy)}>
                {quote.converted_invoice_id ? 'Invoice created' : 'Convert to job + invoice'}
              </button>
            </>
          )}
        </div>
      </header>

      <section className="panel">
        <div className="panel-head">
          <div>
            <h3>{quote.title}</h3>
            {quote.notes ? <p className="field-hint">{quote.notes}</p> : null}
          </div>
          <QuoteStatusPill status={quote.status} />
        </div>

        <table>
          <thead>
            <tr><th>Description</th><th>Qty</th><th>Unit price</th><th>Total</th></tr>
          </thead>
          <tbody>
            {quote.line_items.map((item) => (
              <tr key={item.id}>
                <td>{item.description}</td>
                <td>{item.quantity}</td>
                <td>{money(item.unit_price)}</td>
                <td>{money(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="invoice-total"><span>Total</span><strong>{money(invoiceTotal(quote))}</strong></div>
      </section>

      {quote.status === 'approved' && (
        <section className="panel">
          <div className="panel-head"><h3>Approval</h3></div>
          <dl className="detail-list">
            <dt>Approved by</dt><dd>{quote.approved_by_name || '—'}</dd>
            <dt>Approved at</dt><dd>{quote.approved_at ? date(quote.approved_at) : '—'}</dd>
          </dl>
          {(convertedJob || convertedInvoice) && (
            <dl className="detail-list">
              <dt>Converted job</dt><dd>{convertedJob ? convertedJob.title : '—'}</dd>
              <dt>Converted invoice</dt><dd>{convertedInvoice ? convertedInvoice.number : '—'}</dd>
            </dl>
          )}
        </section>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm === 'decline' ? 'Mark this quote declined?' : 'Mark this quote expired?'}
        message={confirm === 'decline'
          ? 'The client will no longer be able to approve it from the portal.'
          : 'The quote will show as expired in the client portal.'}
        confirmLabel={confirm === 'decline' ? 'Mark declined' : 'Mark expired'}
        onClose={() => setConfirm(null)}
        onConfirm={() => setStatus(confirm)}
      />
    </div>
  );
}
